import _ from 'lodash'
import { DateTime } from 'luxon'

import AuthMixin from '@/components/mixins/auth-mixin'
import ActivateTable from '@/components/pages/activate/table/table-tmpl'
import AssignLicense from '@/components/pages/activate/assign-license/assign-license-tmpl'
import LicenseStatusLabel from '@/components/pages/activate/license-status-label/license-status-label-tmpl'
import ActivateSearchbar from '@/components/pages/activate/activate-searchbar/activate-searchbar-tmpl'
import EventBus from '@/components/mixins/event-bus-mixin'
import { proxyException } from '@/utils/communication'
import { LICENSE_STATES } from '@/components/pages/activate/license-states'
import { LICENSE_STATUS_ICON } from '@/components/pages/activate/license-status-icon'
import { ACTIVATION_ROW_ACTIONS } from './activation-common'
import { filter, sort } from '../table/table-functions'

export const POLLING_ID = 'activation-polling'
export const POLLING_INTERVAL_SECONDS = 15

export default {
  name: 'activation',
  inject: [
    '$utils',
    '$api',
    '$apiError',
    '$pollingManager'
  ],
  components: {
    AssignLicense,
    LicenseStatusLabel,
    ActivateTable,
    ActivateSearchbar
  },
  mixins: [AuthMixin],
  data () {
    return {
      licencesTableColumns: [
        {
          key: 'comment',
          label: this.$t('activation.comment'),
          placeholder: 'Comment',
          type: 'input',
          sortable: true,
          thStylingClasses: ['text-left', 'w-[315px]', 'pr-[66px]']
        },
        {
          key: 'playerSerialNumber',
          label: this.$t('activation.playerSerialNumber'),
          type: 'input',
          sortable: true,
          thStylingClasses: ['text-left', 'w-[300px]', 'pr-[55px]'],
          tdValueStylingClasses: ['font-roboto-mono']
        },
        {
          key: 'license',
          label: this.$t('activation.license'),
          type: 'license',
          sortable: true,
          thStylingClasses: ['text-left', 'w-[167px]', 'pr-[44px]']
        },
        {
          key: 'licenseCode',
          label: this.$t('activation.licenseCode'),
          type: 'text',
          thStylingClasses: ['text-left', 'w-[179px]'],
          tdValueStylingClasses: ['font-roboto-mono']
        },
        {
          key: 'activationDate',
          label: this.$t('activation.activationDate'),
          type: 'text',
          sortable: true,
          thStylingClasses: ['text-left', 'w-[215px]', 'pr-[22px]']
        },
        {
          key: 'action',
          label: '',
          type: 'action',
          thStylingClasses: ['text-left', 'w-[190px]']
        }
      ],
      rawLicenseTableData: [],
      licensesTableData: [],
      filteredVisibleLicensesTableData: [],
      searchText: '',
      isSearchResultEmpty: false,
      pollingInterval: null,
      isTableEmpty: false,
      sortData: {
        columnKey: null,
        order: null
      },
      isCommitingLicense: false
    }
  },
  watch: {},
  computed: {
    hasLicensesTableData () {
      return !_.isEmpty(this.licensesTableData)
    }
  },
  methods: {
    async getLicenses () {
      const { ok: response, error } = await proxyException(() => this.$api.get(`${CONFIG.API_BASE_URL}/licenses/active`))
      if (error) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'error',
          alert: {
            message: error.response ? error.response?.data.message ?? error.message : this.$t('errors.failedToFetchLicenses')
          }
        })
      }

      return { licenses: response.data?.licenses ?? [] }
    },
    async commitLicense (licenseCode = '', data = {}) {
      if (this.isCommitingLicense) {
        return
      }

      this.isCommitingLicense = true
      const responseData = await this.$utils.apiRequest(() => this.$api.put(`${CONFIG.API_BASE_URL}/licenses/active/${licenseCode}`, data), 'Failed to update License', true)
      this.isCommitingLicense = false

      return !_.isEmpty(responseData) ? responseData : null
    },
    async downloadLicense (license) {
      this.$utils.downloadDocument({
        url: `${CONFIG.API_BASE_URL}/licenses/active/${license.licenseCode}`,
        dataType: 'application/xml',
        label: `license-${license.playerSerialNumber}.XML`,
        onError: (error) => {
          EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
            type: 'error',
            alert: {
              message: error.response ? error.response?.data.message ?? error.message : this.$t('errors.failedToDownloadLicense')
            }
          })
        }
      })
    },
    async defaultAction () {
      const { licenses: licensesData } = await this.getLicenses()
      this.setLicensesData(licensesData)

      if (!_.isEmpty(this.licensesTableData)) {
        this.startPolling()
      }
    },
    async keepLicensesDataUpdated () {
      console.log('Pulling License Info updates')
      const { licenses: newLicensesData, error } = await this.getLicenses()
      if (error) {
        // Stop polling on error
        this.stopPolling()
        // On 401 Unauthorized error, will handle the error
        // and re-start polling after token refresh
        this.$apiError.handleUnauthorizedError(error, {
          originalRequest: () => {
            this.startPolling()
          }
        })

        return;
      }
      const prevLicensesData = _.chain(this.rawLicenseTableData)
        .map(record => [record.ActivationCode, record])
        .fromPairs()
        .value()

      const updatedLicensesData = _.map(
        newLicensesData,
        (record) => {
          const previous = prevLicensesData[record.ActivationCode]
          if (previous) {
            // NOTE: this is edge case when we need to force remove player serial number
            // after user suspended license on a player
            if (record.status === LICENSE_STATES.UNASSIGNED) {
              // force-clear the field
              record.serialNumber = ''
            }

            // Merge `record` over `previous`, but only keep keys that exist in `record`
            return _.assign(_.pick(previous, _.keys(record)), record);
          }

          return record
        })

      this.setLicensesData(updatedLicensesData)
    },
    startPolling () {
      if (!this.$pollingManager.isRunning(POLLING_ID)) {
        console.log('Start Polling License Updates data')
        this.$pollingManager.start(POLLING_ID)
      }
    },
    setLicensesData (data) {
      const formattedData = this.formatLicensesData(data)
      this.rawLicenseTableData = data
      this.licensesTableData = formattedData
      this.filteredVisibleLicensesTableData = this.filterVisibleLicensesTableData(formattedData)
      this.isTableEmpty = formattedData.length === 0
    },
    stopPolling () {
      console.log('Stop Polling License Updates data')
      this.$pollingManager.stop(POLLING_ID)
    },
    isRowInProcessingState (license) {
      return [
        ACTIVATION_ROW_ACTIONS.PROCESSING,
        ACTIVATION_ROW_ACTIONS.WAITING_FOR_PLAYER_TO_CONNECT
      ].includes(this.getRowActionValue(license).value)
    },
    isLicenseWithinGracePeriod (license) {
      const currentDate = DateTime.now()
      const graceExpiryDate = !_.isEmpty(license.graceExpiryDate)
        ? DateTime.fromISO(license.graceExpiryDate)
        : null

      // if there's no grace period specified, just return true
      if (graceExpiryDate === null) {
        return true
      }

      return currentDate < graceExpiryDate
    },
    getRowActionValue (license) {
      /**
       * Condition:
       * When now > graceExpiryDate and license status='assigned', "SUSPENDED" button should be visible
       * When license status='suspended', "RESTORE" button should be visible
       * When license status='unmovable' regardless the status, "DOWNLOAD" button should be visible
       * When license status='assign-pending' and online=true spinner with "Processing" text should be visible
       * When license status='assign-pending' and online=false "DOWNLOAD" button should be visible
       * When license online=false, spinner with "Waiting for player to connect" text should be visible
       *
       * Note:
       * Action isn't always represented as a button, it may appear as a spinner with text, for example, "Processing."
       */
      let action = {}

      if (license.status === LICENSE_STATES.ASSIGNED && !this.isLicenseWithinGracePeriod(license)) {
        action = {
          value: ACTIVATION_ROW_ACTIONS.SUSPEND,
          cellComponent: {
            type: 'action',
            text: this.$t('activation.suspend')
          }
        }
      } else if (license.status === LICENSE_STATES.SUSPENDED) {
        action = {
          value: ACTIVATION_ROW_ACTIONS.RESTORE,
          cellComponent: {
            type: 'action',
            text: this.$t('activation.restore')
          }
        }
      } else if (license.status === LICENSE_STATES.UNMOVABLE) {
        action = {
          value: ACTIVATION_ROW_ACTIONS.DOWNLOAD,
          cellComponent: {
            type: 'action',
            text: this.$t('activation.download')
          }
        }
      } else if (license.status === LICENSE_STATES.ASSIGN_PENDING && !license.online) {
        action = {
          value: ACTIVATION_ROW_ACTIONS.DOWNLOAD,
          cellComponent: {
            type: 'action',
            text: this.$t('activation.download')
          }
        }
      } else if (license.status === LICENSE_STATES.ASSIGN_PENDING && license.online) {
        action = {
          value: ACTIVATION_ROW_ACTIONS.PROCESSING,
          cellComponent: {
            type: 'spinner',
            text: this.$t('activation.processing')
          }
        }
      } else if (!license.online && [LICENSE_STATES.REVOKE_PENDING, LICENSE_STATES.SUSPEND_PENDING, LICENSE_STATES.RESTORE_PENDING].includes(license.status)) {
        action = {
          value: ACTIVATION_ROW_ACTIONS.WAITING_FOR_PLAYER_TO_CONNECT,
          cellComponent: {
            type: 'spinner',
            text: this.$t('activation.waitingForPlayerToConnect')
          }
        }
      } else if (license.online && [LICENSE_STATES.REVOKE_PENDING, LICENSE_STATES.SUSPEND_PENDING, LICENSE_STATES.RESTORE_PENDING].includes(license.status)) {
        action = {
          value: ACTIVATION_ROW_ACTIONS.PROCESSING,
          cellComponent: {
            type: 'spinner',
            text: this.$t('activation.processing')
          }
        }
      }

      return action
    },
    getPlayerSerialNumberValue (license) {
      /**
       * In certain cases, the player serial number is non-editable based on the following statuses: "revoke-pending", "suspend-pending", "restore-pending" and "unmovable"
       * Therefore, convert the value into an object for custom component.
       */
      let playerSerialNumber = ''
      const columnClassStyle = _.chain(this.licencesTableColumns).find({ key: 'playerSerialNumber' }).at('tdValueStylingClasses').first().value() ?? []

      if ([LICENSE_STATES.REVOKE_PENDING, LICENSE_STATES.SUSPEND_PENDING, LICENSE_STATES.RESTORE_PENDING].includes(license.status)) {
        playerSerialNumber = {
          value: license?.serialNumber ?? '',
          tdValueStylingClasses: [...columnClassStyle, ...['border-transparent']],
          elAttribute: { disabled: true }
        }
      } else if (!this.isLicenseWithinGracePeriod(license) && [LICENSE_STATES.ASSIGNED, LICENSE_STATES.SUSPENDED].includes(license.status)) {
        playerSerialNumber = {
          value: license?.serialNumber ?? '',
          tdValueStylingClasses: [...columnClassStyle, ...['border-transparent']],
          elAttribute: { disabled: true }
        }
      } else if (license.status === LICENSE_STATES.UNMOVABLE) {
        playerSerialNumber = {
          value: license.serialNumber ?? '',
          tdValueStylingClasses: [...columnClassStyle, ...['border-transparent']],
          elAttribute: { disabled: true }
        }
      } else {
        playerSerialNumber = license?.serialNumber ?? ''
      }

      return playerSerialNumber
    },
    formatLicensesData (licenses) {
      const formattedLicenses = licenses.map(license => {
        const rowId = license?.ActivationCode
          ? `${license.ActivationCode}-${license.status}`
          : this.$utils.generateUniqueID()

        const newLicense = {
          id: `#${rowId}`,
          comment: license?.label,
          playerSerialNumber: this.getPlayerSerialNumberValue(license),
          license: {
            status: (license.status === LICENSE_STATES.UNMOVABLE || !this.isLicenseWithinGracePeriod(license))
              ? LICENSE_STATUS_ICON.LOCKED
              : LICENSE_STATUS_ICON.OK,
            type: license?.type
          },
          licenseCode: license?.ActivationCode,
          licenseType: license?.type,
          activationDate: !_.isEmpty(license.firstPlayerAssignDate) ? DateTime.fromISO(license.firstPlayerAssignDate).toFormat('dd LLL yyyy') : '',
          rawActivationDate: license.firstPlayerAssignDate,
          expanded: false,
          action: this.getRowActionValue(license),
          trStylingClasses: {
            'bg-[#EFEFEF]': this.isRowInProcessingState(license)
          }
        }

        return newLicense
      })

      return [...formattedLicenses]
    },
    filterVisibleLicensesTableData (licenses) {
      const searchableFields = ['playerSerialNumber', 'comment', 'licenseCode', 'licenseType', 'activationDate', 'playerSerialNumber.value']

      let filteredLicenses = filter(licenses, searchableFields, this.searchText)

      const { columnKey, order } = this.sortData

      if (columnKey && order) {
        filteredLicenses = sort(filteredLicenses, columnKey, order)
      }

      return filteredLicenses
    },
    async handleNewAssignedLicense () {
      await this.defaultAction()
    },
    handleSearchText (value) {
      this.searchText = value

      this.$nextTick(_ => {
        this.filteredVisibleLicensesTableData = this.filterVisibleLicensesTableData(this.licensesTableData)
        this.isSearchResultEmpty = value && this.filteredVisibleLicensesTableData.length < 1
      })
    },
    async handleInputFocus (data) {
      this.stopPolling()
    },
    async handleInputBlur (data) {
      this.startPolling()
    },
    async handleInputChange (data) {
      const requestData = {}

      this.stopPolling()
      const selectedRow = _.find(this.licensesTableData, (row) => row.id === data.id)

      if (data.columnKey === 'playerSerialNumber') {
        // edge case when serial number is already empty and there's attempt to remove entered text
        if (
          (selectedRow.playerSerialNumber === '' && data.value === '') ||
          selectedRow.playerSerialNumber === data.value
        ) {
          this.startPolling()
          return
        }

        requestData.serialNumber = data.value
      } else if (data.columnKey === 'comment') {
        if (
          ((!selectedRow.comment || _.isEmpty(selectedRow.comment)) && (!data.value || _.isEmpty(data.value))) ||
          selectedRow.comment === data.value
        ) {
          this.startPolling()
          return
        }

        requestData.label = data.value
      }

      if (_.isEmpty(selectedRow.licenseCode) || _.isEmpty(requestData)) {
        this.startPolling()
        return
      }

      await this.commitLicense(selectedRow.licenseCode, requestData)
      this.startPolling()
    },
    async handleButtonClick (index) {
      const selectedRow = this.filteredVisibleLicensesTableData[index]

      if (_.isEmpty(selectedRow.licenseCode) || _.isEmpty(selectedRow.action)) {
        return
      }

      /**
       * Display a confirmation dialog prior to downloading the license
       */
      if (_.isPlainObject(selectedRow.action) && selectedRow.action.value === ACTIVATION_ROW_ACTIONS.DOWNLOAD) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'warning',
          title: this.$t('confirm.downloadLicense'),
          description: this.$t('downloadLicense.confirmDownloadLicense'),
          alert: {
            message: this.$t('downloadLicense.confirmDownloadLicenseWarning')
          },
          callbacks: {
            eventOnClose: () => {},
            eventOKButtonClick: async () => {
              await this.downloadLicense(selectedRow)
              await this.defaultAction()
            }
          }
        })
      } else if (_.isPlainObject(selectedRow.action) && selectedRow.action.value === ACTIVATION_ROW_ACTIONS.SUSPEND) {
        await this.commitLicense(selectedRow.licenseCode, { serialNumber: null })
        await this.defaultAction()
      } else if (_.isPlainObject(selectedRow.action) && selectedRow.action.value === ACTIVATION_ROW_ACTIONS.RESTORE) {
        // it could be either string or more complex object with `.value` attribute
        const serialNumber = _.isString(selectedRow.playerSerialNumber)
          ? selectedRow.playerSerialNumber
          : selectedRow.playerSerialNumber.value
        await this.commitLicense(selectedRow.licenseCode, { serialNumber })
        await this.defaultAction()
      }
    },
    async handleNewLicenseAssigned () {
      await this.defaultAction()
    },
    handleColumnSort (data) {
      let columnKey = data.columnKey

      if (columnKey === 'license') {
        columnKey = 'licenseType'
      } else if (columnKey === 'activationDate') {
        columnKey = 'rawActivationDate'
      }

      this.sortData = {
        columnKey,
        order: data.order
      }

      if (!data.order) {
        this.filteredVisibleLicensesTableData = this.filterVisibleLicensesTableData(this.licensesTableData)

        return
      }

      this.filteredVisibleLicensesTableData = this.filterVisibleLicensesTableData(this.filteredVisibleLicensesTableData)
    }
  },
  beforeUnmount () {
    this.stopPolling()
    this.$pollingManager.deregister(POLLING_ID)

    if (_.isFunction(this.cleanupVisibilityListenerCallback)) {
      this.cleanupVisibilityListenerCallback()
    }
  },
  mounted () {
    this.$pollingManager.register(
      POLLING_ID,
      async () => {
        await this.keepLicensesDataUpdated()
      },
      POLLING_INTERVAL_SECONDS
    )

    this.isAuthenticated({
      callback: async () => {
        await this.defaultAction()
      }
    })

    // This will pause polling data from the backend when the page/tab is hidden and resume when it becomes visible.
    this.cleanupVisibilityListenerCallback = this.$utils.handleDocumentVisibilityChange(
      () => this.stopPolling(),
      () => {
        this.$nextTick(async () => {
          // Start polling mechanism
          this.startPolling()
        })
      }
    )
  }
}
